import { type FC, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfoIcon from '@assets/img/icons/info.svg?react';
import { useTonAddress, useTonWallet } from '@tonconnect/ui-react';

import { ENV } from '@/common/app/env';
import { ModalContext } from '@/components/Modals/Modal/config';
import { TonConnectButton } from '@/components/TonConnect/TonConnectButton/TonConnectButton';
import { H2, H3, StringSmall } from '@/components/Typography/Typography';
import {
  BlueButton,
  Button,
  SecondaryButton,
} from '@/components/ui/Button/Button';
import { Picture } from '@/components/ui/Picture/Picture';
import { useAppSelector } from '@/redux/store';

import { WithdrawalDetails } from '../WithdrawalDetails/WithdrawalDetails';

import usdPng from '/img/icons/usd.png';
import usdWebp from '/img/icons/usd.webp';

const Wallet: FC = () => {
  const [withdrawalDetailsIsOpen, setWithdrawalDetailsIsOpen] = useState(false);
  const { openModal } = useContext(ModalContext);
  const usdtBalance = useAppSelector((state) => state.user.user?.balance?.usdt);
  const tonsPerUsdt = useAppSelector(
    (state) => state.user.extendedInfo?.exchangeRate?.tonsPerUsd,
  );

  const { t } = useTranslation();
  const wallet = useTonWallet();
  const address = useTonAddress();

  const withdrawIsActive =
    wallet && address && (usdtBalance || 0) >= ENV.MIN_USDT_TO_WITHDRAW;

  const withdrawFunds = (): void => {
    if (!withdrawIsActive) return;
    openModal({ renderKind: 'withdrawModal', props: undefined });
  };

  return (
    <div className="w-full mb-4 bg-surface rounded-lg">
      <TonConnectButton />
      <div className="w-full px-4 pb-[1.125rem]">
        <div className="w-full border-t border-t-white/20 pt-3 flex flex-col">
          <StringSmall className="!texe-[14px] !text-white/40 mb-2">
            {t('My balance')}
          </StringSmall>
          <div className="w-full flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Picture
                className="w-6 h-6 shrink-0"
                img={usdPng}
                webp={usdWebp}
                alt="toncoin"
              />
              <div className="flex gap-2 items-baseline">
                <H2 weight="bold">{usdtBalance || 0}</H2>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!!usdtBalance && !!tonsPerUsdt && (
                <StringSmall className="!text-[14px] !text-white/40 flex gap-[2px]">
                  <img src="/img/icons/ton.svg" alt="" />
                  {`~${(usdtBalance * tonsPerUsdt).toFixed(2)} TON`}
                </StringSmall>
              )}
              <Button
                onClick={() => setWithdrawalDetailsIsOpen((state) => !state)}
                variant="transparent"
                className="!w-auto shrink-0 p-1 relative"
              >
                <InfoIcon className="w-6 h-6 shrink-0" />
                <WithdrawalDetails isOpen={withdrawalDetailsIsOpen} />
              </Button>
            </div>
          </div>
          <div className="w-full flex gap-2">
            <BlueButton
              onClick={withdrawFunds}
              inactive={!withdrawIsActive}
              disabled={!withdrawIsActive}
              className="flex-1 !w-auto"
            >
              <H3 weight="extrabold" className="btn-text">
                {t('Withdraw')}
              </H3>
            </BlueButton>
            <SecondaryButton
              onClick={() =>
                openModal({ renderKind: 'walletEarnDetails', props: undefined })
              }
              className="flex-1 !w-auto"
              text={t('Details')}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export { Wallet };
